import { Box, Text, VStack } from '@chakra-ui/react';
import { TitleBar } from '@resistapp/client/components/bars/title-bar';
import { theme } from '@resistapp/client/components/shared/theme';
import { MaybeApiError } from '@resistapp/client/components/states/error';
import { LoadingView } from '@resistapp/client/components/states/loading';
import { useToastWithoutDebounce } from '@resistapp/client/hooks/use-resistomap-toast';
import { useUser } from '@resistapp/client/contexts/use-user-context';
import { useCountries, useCreateShipment, useOrganisations } from '@resistapp/client/hooks/api';
import { isResistomapAdminUser } from '@resistapp/client/utils/access';
import { OrganisationType } from '@resistapp/common/types';
import { chain, omit } from 'lodash';
import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ShipmentForm, ShipmentFormOrganisation, buildShipmentFormDefaults } from './shipment-form';

export function AdminShipmentView() {
  const { user, userLoading } = useUser();
  const { data: countriesByAlpha3 } = useCountries();
  const { data: organisations, isPending: isOrganisationsPending, error: organisationsError } = useOrganisations();
  const createShipment = useCreateShipment();
  const showToast = useToastWithoutDebounce();
  const queryClient = useQueryClient();
  const isGlobalAdmin = isResistomapAdminUser(user);

  const organisationOptions = useMemo(() => {
    if (!user) {
      return [] as ShipmentFormOrganisation[];
    }

    if (isGlobalAdmin) {
      return chain(organisations ?? [])
        .filter(organisation => organisation.type === OrganisationType.CUSTOMER)
        .sortBy(organisation => organisation.name.toLowerCase())
        .map(organisation => ({
          id: organisation.id,
          name: organisation.name,
          country: organisation.country ?? null,
          pickupAddressLine1: organisation.pickupAddressLine1 ?? null,
          pickupAddressLine2: organisation.pickupAddressLine2 ?? null,
          pickupAddressLine3: organisation.pickupAddressLine3 ?? null,
          pickupPostalCode: organisation.pickupPostalCode ?? null,
          pickupCity: organisation.pickupCity ?? null,
          taxId: organisation.taxId ?? null,
        }))
        .value();
    }

    return chain(user.organisations)
      .filter(organisation => organisation.type === OrganisationType.CUSTOMER)
      .sortBy(organisation => organisation.name.toLowerCase())
      .map(organisation => ({
        id: organisation.id,
        name: organisation.name,
        country: organisation.country ?? null,
        pickupAddressLine1: organisation.pickupAddressLine1 ?? null,
        pickupAddressLine2: organisation.pickupAddressLine2 ?? null,
        pickupAddressLine3: organisation.pickupAddressLine3 ?? null,
        pickupPostalCode: organisation.pickupPostalCode ?? null,
        pickupCity: organisation.pickupCity ?? null,
        taxId: organisation.taxId ?? null,
      }))
      .value();
  }, [isGlobalAdmin, organisations, user]);

  const selectedOrganisation = organisationOptions.length > 0 ? organisationOptions[0] : null;

  const defaultValues = useMemo(() => {
    if (!user || !selectedOrganisation) {
      return null;
    }

    return buildShipmentFormDefaults({
      organisationId: selectedOrganisation.id,
      contactName: `${user.firstName} ${user.lastName}`.trim(),
      contactEmail: user.email,
      contactTelephoneNumber: user.telephoneNumber ?? null,
      pickupCountry: selectedOrganisation.country ?? null,
      pickupAddressLine1: selectedOrganisation.pickupAddressLine1 ?? null,
      pickupAddressLine2: selectedOrganisation.pickupAddressLine2 ?? null,
      pickupAddressLine3: selectedOrganisation.pickupAddressLine3 ?? null,
      pickupPostalCode: selectedOrganisation.pickupPostalCode ?? null,
      pickupCity: selectedOrganisation.pickupCity ?? null,
      taxId: selectedOrganisation.taxId ?? null,
    });
  }, [selectedOrganisation, user]);

  if (userLoading || isOrganisationsPending) {
    return <LoadingView />;
  }

  if (!user) {
    return (
      <Box padding={theme.spacing[6]}>
        <Text>Please log in to create a shipment request.</Text>
      </Box>
    );
  }

  if (isGlobalAdmin && organisationsError) {
    return (
      <Box padding={theme.spacing[6]}>
        <MaybeApiError error={organisationsError} />
      </Box>
    );
  }

  if (!defaultValues || organisationOptions.length === 0) {
    return (
      <Box padding={theme.spacing[6]}>
        <Text color="gray.600">No eligible customer organisation found for shipment request.</Text>
      </Box>
    );
  }

  return (
    <div style={{ marginBottom: theme.spacing[16] }}>
      <TitleBar title="Request DNA Shipment Pickup" $extraWide={true} />
      <VStack align="stretch" spacing={4} padding={theme.spacing[4]}>
        <ShipmentForm
          mode="create"
          defaultValues={defaultValues}
          countriesByAlpha3={countriesByAlpha3}
          organisationOptions={organisationOptions}
          disableOrganisationSelect={organisationOptions.length <= 1}
          submitLabel="Submit shipment request"
          isSubmitting={createShipment.isPending}
          readOnly={createShipment.isSuccess}
          onSubmit={async values => {
            const createPayload = omit(values, ['status']);
            await createShipment.mutateAsync(createPayload);
            await queryClient.invalidateQueries({ queryKey: ['/shipping/workbooks'] });
            showToast({
              title: 'Shipment request submitted',
              description: 'Your pickup request has been saved.',
              status: 'success',
              duration: 7000,
            });
          }}
        />
        <MaybeApiError error={createShipment.error} />
      </VStack>
    </div>
  );
}
